import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useNotification } from '../../../components/framework/NotificationContext';
import EmailCenterNav from './components/EmailCenterNav';
import ComposerPanel from './components/ComposerPanel';
import RecipientsPanel from './components/RecipientsPanel';
import DeliveryOptionsPanel from './components/DeliveryOptionsPanel';
import PreviewPanel from './components/PreviewPanel';
import TemplatesPanel from './components/TemplatesPanel';
import HistoryPanel from './components/HistoryPanel';
import AuditLogPanel from './components/AuditLogPanel';
import AnalyticsPanel from './components/AnalyticsPanel';
import useAutosaveDraft from './hooks/useAutosaveDraft';
import {
    saveDraft as saveDraftApi,
    sendCampaign,
    sendTestEmail,
} from '../../../services/emailCampaignService';
import './email-center.css';

const SECTIONS_WITH_HISTORY_TABLE = ['drafts', 'scheduled', 'sent', 'failed', 'history'];

const EmailCenterPage = () => {
    const { section } = useParams();
    const navigate = useNavigate();
    const { success, error: notifyError } = useNotification();
    const activeSection = section || 'compose';

    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [customVariables, setCustomVariables] = useState({});
    const [recipientState, setRecipientState] = useState({ filterPayload: null, estimatedCount: 0 });
    const [sending, setSending] = useState(false);

    // One idempotencyKey per compose session: the backend upserts a DRAFT on repeat
    // draft-saves with the same key, and transitions that same DRAFT to SENDING/SCHEDULED
    // on Send. It must be regenerated whenever the compose form resets (after a send),
    // since idempotency_key is unique and a sent/scheduled campaign is never reused.
    const idempotencyKeyRef = useRef(crypto.randomUUID());

    const handleNavigate = (key) => {
        navigate(key === 'compose' ? '/admin/email' : `/admin/email/${key}`);
    };

    const resetComposeState = () => {
        setSubject('');
        setBody('');
        setCustomVariables({});
        setRecipientState({ filterPayload: null, estimatedCount: 0 });
        idempotencyKeyRef.current = crypto.randomUUID();
    };

    const buildCampaignPayload = useCallback(
        (deliveryOptions = {}) => {
            const scheduled = deliveryOptions.sendMode === 'SCHEDULED';
            return {
                idempotencyKey: idempotencyKeyRef.current,
                subjectTemplate: subject,
                bodyHtmlTemplate: body,
                customVariables,
                recipientFilter: recipientState.filterPayload,
                replyTo: deliveryOptions.replyTo || null,
                cc: deliveryOptions.cc || null,
                bcc: deliveryOptions.bcc || null,
                priority: deliveryOptions.priority || 'NORMAL',
                sendMode: scheduled ? 'SCHEDULED' : 'IMMEDIATE',
                scheduledAt: scheduled ? deliveryOptions.scheduledAt : null,
            };
        },
        [subject, body, customVariables, recipientState]
    );

    // Autosave the current compose draft (debounced) while on the Compose section.
    const draftData = useMemo(
        () => ({ subject, body, customVariables, recipientFilter: recipientState.filterPayload }),
        [subject, body, customVariables, recipientState]
    );
    const saveDraft = useCallback(async (data) => {
        if (!data.subject && !data.body) return;
        await saveDraftApi(buildCampaignPayload());
    }, [buildCampaignPayload]);
    const { isDirty, isSaving } = useAutosaveDraft(draftData, saveDraft, { enabled: activeSection === 'compose' });

    // Keyboard shortcuts: Ctrl/Cmd+S = save draft, Ctrl/Cmd+Enter = send.
    const sendButtonRef = useRef(null);
    useEffect(() => {
        const handler = (e) => {
            const isMeta = e.ctrlKey || e.metaKey;
            if (!isMeta) return;
            if (e.key === 's') {
                e.preventDefault();
                saveDraft(draftData);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                sendButtonRef.current?.click();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [draftData, saveDraft]);

    const handleSend = async (deliveryOptions) => {
        setSending(true);
        try {
            await sendCampaign(buildCampaignPayload(deliveryOptions));
            success(deliveryOptions.sendMode === 'SCHEDULED' ? 'Email scheduled' : 'Email sent');
            resetComposeState();
            navigate('/admin/email/history');
        } catch (err) {
            notifyError(err.message || 'Failed to send email');
        } finally {
            setSending(false);
        }
    };

    const handleSaveDraft = async (deliveryOptions) => {
        try {
            await saveDraftApi(buildCampaignPayload(deliveryOptions));
            success('Draft saved');
        } catch (err) {
            notifyError(err.message || 'Failed to save draft');
        }
    };

    const handleSendTest = async () => {
        try {
            await sendTestEmail({ subjectTemplate: subject, bodyHtmlTemplate: body, customVariables });
            success('Test email sent to your account');
        } catch (err) {
            notifyError(err.message || 'Failed to send test email');
        }
    };

    const handleTemplateApply = (template) => {
        setSubject(template.subjectTemplate || '');
        setBody(template.bodyHtmlTemplate || '');
    };

    const renderMainContent = () => {
        if (activeSection === 'compose') {
            return (
                <div className="email-center-compose-grid">
                    <div>
                        <ComposerPanel
                            subject={subject}
                            body={body}
                            customVariables={customVariables}
                            onSubjectChange={setSubject}
                            onBodyChange={setBody}
                            onTemplateApply={handleTemplateApply}
                            onCustomVariablesChange={setCustomVariables}
                        />
                        <PreviewPanel subject={subject} html={body} />
                    </div>
                    <div>
                        <RecipientsPanel onRecipientsChange={setRecipientState} />
                        <DeliveryOptionsPanel
                            recipientCount={recipientState.estimatedCount || 0}
                            onSend={handleSend}
                            onSaveDraft={handleSaveDraft}
                            onSendTest={handleSendTest}
                            sending={sending}
                        />
                        <div ref={sendButtonRef} style={{ display: 'none' }} />
                        <div className="small text-muted">
                            {isSaving ? 'Saving draft…' : isDirty ? 'Unsaved changes' : 'All changes saved'}
                        </div>
                    </div>
                </div>
            );
        }
        if (activeSection === 'templates') return <TemplatesPanel />;
        if (activeSection === 'analytics') return <AnalyticsPanel />;
        if (activeSection === 'audit') return <AuditLogPanel />;
        if (SECTIONS_WITH_HISTORY_TABLE.includes(activeSection)) return <HistoryPanel section={activeSection} />;
        return <HistoryPanel section="history" />;
    };

    return (
        <AdminLayout title="Email Center" subtitle="Compose, schedule and track administrative communications.">
            <div className="email-center-grid">
                <EmailCenterNav activeSection={activeSection} onNavigate={handleNavigate} />
                <div>{renderMainContent()}</div>
            </div>
        </AdminLayout>
    );
};

export default EmailCenterPage;
